'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { CertifyingLetters } from '@module/mdrt-org/shared/block-components/membership-requirements-block/components/certifying-letters/certifying-letters';
import { type MembershipRequirementsBlockWithFallbacks } from '@module/mdrt-org/shared/block-components/membership-requirements-block/utils/helpers/add-fallbacks-to-membership-requirements-block';
import { getDefaultCountryOption } from '@module/mdrt-org/shared/block-components/membership-requirements-block/utils/helpers/get-default-country-option';
import { useMembershipRequirementsOptions } from '@module/mdrt-org/shared/block-components/membership-requirements-block/utils/hooks/use-membership-requirement-options';
import { type SelectOption } from '@shared/utils/types/select-option';
import { Loader } from '../../../../components/loader/loader';
import { SelectField } from '../../../../components/select-field/select-field';
import { OrgSharedDataTag } from '../../../../utils/data/constants/tags';
import { getMembershipRequirementBlockByYear } from '../../../../utils/data/get-membership-requirement-block-by-year';
import { getMembershipRequirementsPerCountry } from '../../../../utils/data/get-membership-requirements-per-country';
import { type NextSiteSettingsPage } from '../../../../utils/data/graphql/_generated/gql-generated';
import { type MembershipRequirementDimensions, type MembershipRequirementsPerYear } from '../../../../utils/types/membership-requirements';
import { EthicalCode } from '../ethical-code/ethical-code';
import { MembershipDues } from '../membership-dues/membership-dues';
import { ProductionRequirements } from '../production-requirements/production-requirements';
import styles from './membership-requirements.module.scss';
type MembershipRequirementsProps = {
  blockData: MembershipRequirementsBlockWithFallbacks;
  dimensions: MembershipRequirementDimensions;
  siteSettingsData?: NextSiteSettingsPage;
  userCountry: {
    countryCode: string;
    countryName: string;
  };
};
export const MembershipRequirements = t0 => {
  const $ = _c(73);
  const {
    blockData,
    dimensions,
    siteSettingsData,
    userCountry
  } = t0;
  const [dropdownsReady, setDropdownsReady] = useState(false);
  const {
    countryOptions,
    yearOptions
  } = useMembershipRequirementsOptions(dimensions);
  let t1;
  if ($[0] !== countryOptions || $[1] !== userCountry) {
    t1 = getDefaultCountryOption({
      countryOptions,
      userCountry
    });
    $[0] = countryOptions;
    $[1] = userCountry;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [selectedCountry, setSelectedCountry] = useState(t1);
  const [selectedYear, setSelectedYear] = useState(yearOptions?.[0] ?? null);
  const t2 = Boolean(selectedCountry?.value) && Boolean(selectedYear?.value);
  let t3;
  if ($[3] !== selectedCountry?.countryName || $[4] !== selectedCountry?.value) {
    t3 = () => getMembershipRequirementsPerCountry({
      countryCode: selectedCountry?.value ?? "",
      countryName: selectedCountry?.countryName ?? ""
    });
    $[3] = selectedCountry?.countryName;
    $[4] = selectedCountry?.value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = `${OrgSharedDataTag.GET_MEMBERSHIP_REQUIREMENTS}_${selectedCountry?.value}_${selectedCountry?.label}`;
  let t5;
  if ($[6] !== t4) {
    t5 = [OrgSharedDataTag.GET_MEMBERSHIP_REQUIREMENTS, t4];
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== selectedYear?.value) {
    t6 = allYears => allYears?.requirements.find(yearData => yearData.year === selectedYear?.value);
    $[8] = selectedYear?.value;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t5 || $[13] !== t6) {
    t7 = {
      enabled: t2,
      queryFn: t3,
      queryKey: t5,
      select: t6
    };
    $[10] = t2;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const {
    data: countryYearData,
    error,
    isFetching: isCountryYearDataFetching
  } = useQuery(t7);
  const t8 = Boolean(selectedYear?.value);
  let t9;
  if ($[15] !== selectedYear?.value) {
    t9 = () => getMembershipRequirementBlockByYear(selectedYear?.value as number);
    $[15] = selectedYear?.value;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = `${OrgSharedDataTag.GET_MEMBERSHIP_REQUIREMENTS}_block_${selectedYear?.value}`;
  let t11;
  if ($[17] !== t10) {
    t11 = [OrgSharedDataTag.GET_MEMBERSHIP_REQUIREMENTS, t10];
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t8 || $[21] !== t9) {
    t12 = {
      enabled: t8,
      queryFn: t9,
      queryKey: t11,
      select: _temp
    };
    $[19] = t11;
    $[20] = t8;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const {
    data: selectedYearMembershipRequirementBlockData,
    isFetching: isYearBlockDataFetching
  } = useQuery(t12);
  const isFetching = isCountryYearDataFetching || isYearBlockDataFetching;
  let t13;
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setDropdownsReady(true);
    };
    t14 = [];
    $[23] = t13;
    $[24] = t14;
  } else {
    t13 = $[23];
    t14 = $[24];
  }
  useEffect(t13, t14);
  let t15;
  if ($[25] !== blockData) {
    t15 = blockData?.ProductionRequirementsAnchorLink && {
      id: blockData.ProductionRequirementsAnchorLink
    };
    $[25] = blockData;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== isFetching) {
    t16 = isFetching && <Loader allowScroll={true} isInner={true} />;
    $[27] = isFetching;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const t17 = blockData?.ProductionRequirementsStepLabel;
  let t18;
  if ($[29] !== t17) {
    t18 = <h2>{t17}</h2>;
    $[29] = t17;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== countryOptions || $[32] !== dropdownsReady || $[33] !== selectedCountry || $[34] !== siteSettingsData?.CountryLabel) {
    t19 = <div className={styles["header__country-selector"]}>{dropdownsReady ? <SelectField id="country-selector" isRequired={true} label={siteSettingsData?.CountryLabel ?? "Country"} name="country" onChange={value => setSelectedCountry(value as SelectOption<string> & {
        countryName: string;
      })} options={countryOptions} value={selectedCountry} withoutController={true} /> : <Skeleton className={styles["header__dropdown-skeleton"]} />}</div>;
    $[31] = countryOptions;
    $[32] = dropdownsReady;
    $[33] = selectedCountry;
    $[34] = siteSettingsData?.CountryLabel;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== dropdownsReady || $[37] !== selectedYear || $[38] !== siteSettingsData?.YearLabel || $[39] !== yearOptions) {
    t20 = <div className={styles["header__year-selector"]}>{dropdownsReady ? <SelectField id="year-selector" isRequired={true} label={siteSettingsData?.YearLabel ?? "Year"} name="year" onChange={value_0 => setSelectedYear(value_0 as SelectOption<number>)} options={yearOptions} value={selectedYear} withoutController={true} /> : <Skeleton className={styles["header__dropdown-skeleton"]} />}</div>;
    $[36] = dropdownsReady;
    $[37] = selectedYear;
    $[38] = siteSettingsData?.YearLabel;
    $[39] = yearOptions;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t18 || $[42] !== t19 || $[43] !== t20) {
    t21 = <div className={styles.header}>{t18}{t19}{t20}</div>;
    $[41] = t18;
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== blockData || $[46] !== countryYearData || $[47] !== error || $[48] !== isFetching || $[49] !== selectedYearMembershipRequirementBlockData) {
    t22 = <ProductionRequirements blockData={blockData} countryYearData={countryYearData} error={error} isFetching={isFetching} yearBlockData={selectedYearMembershipRequirementBlockData} />;
    $[45] = blockData;
    $[46] = countryYearData;
    $[47] = error;
    $[48] = isFetching;
    $[49] = selectedYearMembershipRequirementBlockData;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t15 || $[52] !== t16 || $[53] !== t21 || $[54] !== t22) {
    t23 = <section {...t15}>{t16}{t21}{t22}</section>;
    $[51] = t15;
    $[52] = t16;
    $[53] = t21;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== blockData || $[57] !== countryYearData || $[58] !== isFetching || $[59] !== selectedYearMembershipRequirementBlockData) {
    t24 = <CertifyingLetters blockData={blockData} countryYearData={countryYearData} isFetching={isFetching} yearBlockData={selectedYearMembershipRequirementBlockData} />;
    $[56] = blockData;
    $[57] = countryYearData;
    $[58] = isFetching;
    $[59] = selectedYearMembershipRequirementBlockData;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== blockData) {
    t25 = <EthicalCode blockData={blockData} />;
    $[61] = blockData;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== blockData || $[64] !== countryYearData || $[65] !== isFetching || $[66] !== selectedYearMembershipRequirementBlockData) {
    t26 = <MembershipDues blockData={blockData} countryYearData={countryYearData} isFetching={isFetching} yearBlockData={selectedYearMembershipRequirementBlockData} />;
    $[63] = blockData;
    $[64] = countryYearData;
    $[65] = isFetching;
    $[66] = selectedYearMembershipRequirementBlockData;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== t23 || $[69] !== t24 || $[70] !== t25 || $[71] !== t26) {
    t27 = <div className={styles["requirements-wrapper"]}>{t23}{t24}{t25}{t26}</div>;
    $[68] = t23;
    $[69] = t24;
    $[70] = t25;
    $[71] = t26;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  return t27;
};
function _temp(data) {
  return data ?? undefined;
}